import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'

import { cn } from '@/lib/utils'

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 max-w-xs rounded-md border bg-popover px-3 py-2 text-xs leading-relaxed text-popover-foreground shadow-md outline-none data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=delayed-open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

/**
 * Info icon that opens a tooltip on hover and on click.
 *
 * Radix closes the tooltip on touch, so on a phone the hover trigger alone never
 * shows anything; keeping the open state here and toggling it on click makes the
 * same icon work with mouse, keyboard and finger.
 */
const InfoTooltip = React.forwardRef<
  HTMLButtonElement,
  {
    children: React.ReactNode
    /** Plain text used as the accessible name, and as the tooltip body when no `content` is given. */
    label: string
    /** Richer tooltip body (several lines, emphasis...). Falls back to `label`. */
    content?: React.ReactNode
    className?: string
    side?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>['side']
    align?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>['align']
  }
>(({ children, label, content, className, side = 'top', align = 'center' }, ref) => {
  const [open, setOpen] = React.useState(false)

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            ref={ref}
            type="button"
            aria-label={label}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setOpen((prev) => !prev)
            }}
            className={cn(
              'inline-flex items-center justify-center text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full',
              className
            )}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} align={align} onClick={(e) => e.stopPropagation()}>
          {content ?? label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})
InfoTooltip.displayName = 'InfoTooltip'

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, InfoTooltip }
